/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useLayoutEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useEditorRootEl, useForkRef } from "../../../hooks";
import { setRef } from "../../../utils";
import { isFunction } from "@adapptio/lib-shared";

export interface IPortalProps {
	children?: React.ReactNode;
	container?: Element | (() => Element | null) | null;
	disablePortal?: boolean;
}

function getContainer(container: IPortalProps["container"]): Element {
	return isFunction(container) ? container() : container;
}

export const Portal = React.forwardRef<HTMLDivElement, IPortalProps>(
	({ container, children, disablePortal }, ref) => {
		const editorRootRef = useEditorRootEl();
		const [ mountNode, setMountNode ] = useState<Element>(null);
		const forkRef = useForkRef(React.isValidElement(children) ? children["ref"] : null, ref);

		useLayoutEffect(() => {
			if (!disablePortal) {
				setMountNode(getContainer(container) || editorRootRef?.current || document.body);
			}
		}, [ container, disablePortal ]);

		useLayoutEffect(() => {
			if (mountNode && !disablePortal) {
				setRef(ref, mountNode);

				return () => {
					setRef(ref, null);
				};
			}

			return undefined;
		}, [ ref, mountNode, disablePortal ]);

		if (disablePortal) {
			if (React.isValidElement(children)) {
				return React.cloneElement(children, {
					ref: forkRef
				} as React.HTMLAttributes<unknown>);
			}

			return null;
		}

		return mountNode ? ReactDOM.createPortal(children, mountNode) : null;
	}
);

Portal.displayName = "Portal";
